import api from './api';
import Axios from 'axios';
import { getJwt } from '@app/helpers';
import { notification } from '@digital/ui';

export interface NotificacaoPendenciaContrato {
  id: string;
  contratoId: string;
  competenciaMonitoramento: string;
  situacaoContrato: string;
  entregaDocumento: string;
  pendenciasAtuais: number;
  pendenciasAnteriores: number;
  dataNotificacaoGestor: string | null;
  statusNotificacao: string;
  numeroProtocolo: string | null;
  temDocumentoCritico: boolean;
  comentarioReprovacao: string | null;
  dataEnvioFornecedor: string | null;
}

export interface PendenciaDocumental {
  id: string;
  competenciaMonitoramento: string;
  competenciaDocumento: string;
  nomeDocumento: string;
  tipoInconsistencia: string;
  inconsistencia: string;
  acaoCorretiva: string;
  status: string;
  critico: boolean;
  quantidadeNotificacoes: number;
}

export interface PendenciasContratoResult {
  notificacoes: NotificacaoPendenciaContrato[];
  podeAprovarNotificacoes: boolean;
  podeGerirAssinaturaExpiradaOuRecusada: boolean;
  notificacaoPausada?: boolean;
  documentoPausa?: string | null;
  dataPausa?: string | null;
  podeReativarNotificacao?: boolean;
}

export interface PendenciaGeralItem {
  numeroContrato: number;
  competenciaMonitoramento: string;
  competenciaDocumento: string;
  nomeDocumento: string;
  tipoInconsistencia: string;
  inconsistencia: string;
  status: string;
  critico: boolean;
  quantidadeNotificacoes: number;
}

export const getPendenciasByContrato = (contratoId: string) =>
  api.get<PendenciasContratoResult>(`pendenciaDocumental/contrato/${contratoId}`);

export const getNaoConformesTodos = (contratoId: string) =>
  api.get<PendenciaDocumental[]>(`pendenciaDocumental/contrato/${contratoId}/naoConformes`);

export const exportarNaoConformesContrato = (contratoId: string) => {
  const url = window.location.origin + '/api/pendenciaDocumental/contrato/' + contratoId + '/naoConformes/exportar';
  return Axios.get(url, {
    responseType: 'blob',
    headers: { Authorization: 'Bearer ' + getJwt() },
  }).then(function(resp) {
    const tipo = resp.data && resp.data.type ? resp.data.type : '';
    if (tipo.indexOf('json') !== -1 || tipo.indexOf('html') !== -1) {
      return resp.data.text().then((text: string) => {
        let msg = 'Houve um erro ao exportar.';
        try {
          const parsed = JSON.parse(text);
          msg = (parsed && (parsed.message || parsed.title)) || msg;
        } catch { /* keep default */ }
        notification.error(msg);
      });
    }
    const blobUrl = window.URL.createObjectURL(new Blob([resp.data]));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', 'pendencias_documentais_contrato.xlsx');
    link.setAttribute('id', 'download-pendencias-contrato');
    document.body.appendChild(link);
    link.click();
    const el = document.getElementById('download-pendencias-contrato');
    if (el) el.outerHTML = '';
  }).catch(function(err: any) {
    let msg = 'Houve um erro ao exportar.';
    if (err && err.response && err.response.data) {
      const data = err.response.data;
      if (data instanceof Blob) {
        data.text().then((text: string) => {
          try {
            const parsed = JSON.parse(text);
            msg = (parsed && (parsed.message || parsed.title)) || msg;
          } catch { /* keep default */ }
          notification.error(msg);
        });
        return;
      }
      msg = (data && (data.message || data.title)) || msg;
    }
    notification.error(msg);
  });
};

export interface PaginationResult<T> {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  items: T[];
}

export interface PendenciasGeralFiltros {
  numerosContrato: number[];
  competenciasDocumento: string[];
  nomesDocumento: string[];
  tiposInconsistencia: string[];
}

export interface PendenciasGeralComFiltrosResult {
  pendencias: PaginationResult<PendenciaGeralItem>;
  filtros: PendenciasGeralFiltros;
}

export const getPendenciasGeral = (params: {
  competenciaDocumento?: string;
  numeroContrato?: number;
  nomeDocumento?: string;
  tipoInconsistencia?: string;
  page?: number;
  size?: number;
}) => {
  const qs = new URLSearchParams();
  if (params.competenciaDocumento) qs.set('competenciaDocumento', params.competenciaDocumento);
  if (params.numeroContrato) qs.set('numeroContrato', String(params.numeroContrato));
  if (params.nomeDocumento) qs.set('nomeDocumento', params.nomeDocumento);
  if (params.tipoInconsistencia) qs.set('tipoInconsistencia', params.tipoInconsistencia);
  qs.set('page', String(params.page || 1));
  qs.set('size', String(params.size || 20));
  return api.get<PendenciasGeralComFiltrosResult>(`pendenciaDocumental/geral?${qs.toString()}`);
};

export const notificarGestor = (notificacaoId: string) =>
  api.post<any>(`pendenciaDocumental/notificarGestor/${notificacaoId}`, {});

export const notificarFornecedor = (contratoId: string, competencia: string) =>
  api.post<any>(`pendenciaDocumental/notificarFornecedor/${contratoId}?competencia=${encodeURIComponent(competencia)}`, {});

export const aprovarNotificacao = (id: string) =>
  api.post<any>(`pendenciaDocumental/notificacao/${id}/aprovar`, {});

export const reprovarNotificacao = (id: string, comentario: string) =>
  api.post<any>(`pendenciaDocumental/notificacao/${id}/reprovar`, { comentario });

/** Gera URL DocuSign sob demanda (válida por poucos minutos). */
export const getUrlAssinaturaNotificacao = (id: string) =>
  api.post<{ urlAssinatura?: string; valid?: boolean }>(`pendenciaDocumental/notificacao/${id}/urlAssinatura`, {});

export const reativarNotificacoesPendencia = (contratoId: string) =>
  api.post<any>(`pendenciaDocumental/contrato/${contratoId}/reativarNotificacoes`, {});

export interface NotificacaoPendenciaGeralItem {
  id: string;
  contratoId: string;
  numeroContrato: number | null;
  competenciaMonitoramento: string;
  numeroProtocolo: string | null;
  statusNotificacao: string;
  dataEnvioFornecedor: string | null;
  dataNotificacaoGestor: string | null;
  tipoCarta: string | null;
  temDocumentoCritico: boolean;
  comentarioReprovacao: string | null;
  administradorContrato: string | null;
}

export interface NotificacoesGeralResult {
  notificacoes: PaginationResult<NotificacaoPendenciaGeralItem>;
  numerosContrato: number[];
  administradoresContrato: string[];
  competencias: string[];
}

export const exportarNotificacoes = (params?: {
  numeroContrato?: number;
  competencia?: string;
  status?: string;
  administradorContrato?: string;
}) => {
  const qs = new URLSearchParams();
  if (params && params.numeroContrato) qs.set('numeroContrato', String(params.numeroContrato));
  if (params && params.competencia) qs.set('competencia', params.competencia);
  if (params && params.status) qs.set('status', params.status);
  if (params && params.administradorContrato) qs.set('administradorContrato', params.administradorContrato);
  const url = window.location.origin + '/api/pendenciaDocumental/notificacoes/exportar?' + qs.toString();
  return Axios.get(url, {
    responseType: 'blob',
    headers: { Authorization: 'Bearer ' + getJwt() },
  }).then(function(resp) {
    const tipo = resp.data && resp.data.type ? resp.data.type : '';
    if (tipo.indexOf('json') !== -1 || tipo.indexOf('html') !== -1) {
      notification.error('Houve um erro ao exportar.');
      return;
    }
    const blobUrl = window.URL.createObjectURL(new Blob([resp.data]));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', 'notificacoes_pendencias.xlsx');
    link.setAttribute('id', 'download-notificacoes');
    document.body.appendChild(link);
    link.click();
    const el = document.getElementById('download-notificacoes');
    if (el) el.outerHTML = '';
  }).catch(function() {
    notification.error('Houve um erro ao exportar.');
  });
};

export const exportarPendenciasGeral = (params?: {
  numeroContrato?: number;
  competenciaDocumento?: string;
  nomeDocumento?: string;
  tipoInconsistencia?: string;
}) => {
  const qs = new URLSearchParams();
  if (params && params.numeroContrato) qs.set('numeroContrato', String(params.numeroContrato));
  if (params && params.competenciaDocumento) qs.set('competenciaDocumento', params.competenciaDocumento);
  if (params && params.nomeDocumento) qs.set('nomeDocumento', params.nomeDocumento);
  if (params && params.tipoInconsistencia) qs.set('tipoInconsistencia', params.tipoInconsistencia);
  const url = window.location.origin + '/api/pendenciaDocumental/geral/exportar?' + qs.toString();
  return Axios.get(url, {
    responseType: 'blob',
    headers: { Authorization: 'Bearer ' + getJwt() },
  }).then(function(resp) {
    const tipo = resp.data && resp.data.type ? resp.data.type : '';
    if (tipo.indexOf('json') !== -1 || tipo.indexOf('html') !== -1) {
      notification.error('Houve um erro ao exportar.');
      return;
    }
    const blobUrl = window.URL.createObjectURL(new Blob([resp.data]));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', 'pendencias_documentais.xlsx');
    link.setAttribute('id', 'download-pendencias');
    document.body.appendChild(link);
    link.click();
    const el = document.getElementById('download-pendencias');
    if (el) el.outerHTML = '';
  }).catch(function() {
    notification.error('Houve um erro ao exportar.');
  });
};

export const getNotificacoesGeral = (params?: {
  numeroContrato?: number;
  competencia?: string;
  status?: string;
  administradorContrato?: string;
  page?: number;
  size?: number;
}) => {
  const qs = new URLSearchParams();
  if (params && params.numeroContrato) qs.set('numeroContrato', String(params.numeroContrato));
  if (params && params.competencia) qs.set('competencia', params.competencia);
  if (params && params.status) qs.set('status', params.status);
  if (params && params.administradorContrato) qs.set('administradorContrato', params.administradorContrato);
  qs.set('page', String((params && params.page) || 1));
  qs.set('size', String((params && params.size) || 20));
  return api.get<NotificacoesGeralResult>('pendenciaDocumental/notificacoes?' + qs.toString());
};
